import { Component, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService, AuthService, I18nService, ThemeService, ToastService, Response, ErrorResponse } from '@toteimlib/angular-lib';
import { ToastModule } from '@openng/optimus-ui/toast';
import { CardModule } from '@openng/optimus-ui/card';
import { fromEvent, map, merge, of, Subscription } from 'rxjs';
import { PropertyService } from './services/property.service';
import { FirebaseService } from './services/firebase.service';
import { environment } from 'environments/environment';
import Intercom from '@intercom/messenger-js-sdk';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    ToastModule,
    CardModule
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {

  theme: Signal<string> = signal('light');

  networkStatusSubscription: Subscription = Subscription.EMPTY;
  routerSubscription: Subscription = Subscription.EMPTY;

  showMaintenance: WritableSignal<boolean> = signal(false);
  maintenanceMessage: WritableSignal<string | null> = signal(null);
  networkStatus: WritableSignal<boolean> = signal(false);

  constructor(private translateService: TranslateService, private propertyService: PropertyService, private toastService: ToastService, private i18nService: I18nService, private authService: AuthService, private apiService: ApiService, private themeService: ThemeService, private router: Router, private firebaseService: FirebaseService) {
    const userLang: string = this.i18nService.getLang()();
    const hash: string = window.location.hash;

    this.theme = this.themeService.getTheme();

    // Handle retro-compatibility with AngularJS routing
    if (hash.startsWith('#!/')) {
      router.navigateByUrl(hash.substring(2));
    }

    this.translateService.addLangs(environment.langs);
    this.translateService.setDefaultLang('fr');

    this.apiService.initialize(environment.api_url, environment.version, 'property_id', '/dashboard/home', '/login');

    this.apiService.addBeforeMiddleware((res: Response | ErrorResponse): boolean => {
      if (res.error && res.error === 451) {
        this.propertyService.removeProperty();
        this.toastService.showError(res.message);

        return false;
      }

      return true;
    });

    this.authService.setLocalStorageList([
      'api_token',
      'user',
      'property_id',
      'property'
    ]);

    if (environment.langs.includes(userLang)) {
      this.translateService.use(userLang);
    }
    else {
      this.translateService.use('fr');
    }

    if (environment.production) {
      Intercom({
        app_id: environment.intercom_app_id,
        Origin: 'ToteimResident'
      });
    }

    this.routerSubscription = router.events.subscribe((val: any): void => {
      if (val instanceof NavigationStart) {
        this.getRemoteConfig();
      }
    });
  }

  ngOnInit(): void {
    this.checkNetworkStatus();
    this.getRemoteConfig();
  }

  ngOnDestroy(): void {
    this.networkStatusSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  getRemoteConfig(): void {
    this.firebaseService.isMaintenanceMode().then((res: boolean): void => {
      this.showMaintenance.set(res);
    });

    this.firebaseService.getMaintenanceMessage(this.i18nService.getLang()()).then((res: string | null): void => {
      this.maintenanceMessage.set(res);
    });
  }

  checkNetworkStatus(): void {
    this.networkStatus.set(navigator.onLine);

    this.networkStatusSubscription = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map((): boolean => navigator.onLine))
      .subscribe((status: boolean): void => {
        this.networkStatus.set(status);
      });
  }

}
